import {call, delay, put, select, SagaReturnType} from 'redux-saga/effects';
import types from '../types';
import ApiService from 'api/ApiService';
import api from 'api';
import {DialogTypes} from 'common/names';
import {action, Action, asyncType} from 'redux/helpers';
import {ICreateOpenPGPKeyPair} from 'types/diplomat/keys/CreateOpenPGPKeyPair';
import {pushErrorToast, pushSuccessToast} from 'features/notifications/redux/actions';
import selectors from 'features/layout/redux/selectors';
import {IAddOpenPGPSubKey} from 'types/diplomat/keys/AddOpenPGPSubKey';
import {isDialogActive} from 'redux/common/saga/isDialogActiveSaga';
import {closeDialog} from 'redux/common/saga/closeDialogSaga';
import {unhandedSagaError} from 'redux/common';
import {IDiplomatObject} from 'types/DiplomatObject';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import i18n from 'i18n';
import history from 'redux/history';
import {packId} from 'common/diplomatIdUtil';
import {convertDaysToSeconds} from 'common/dateUtils';
import dayjs from 'dayjs';

function* pollKeyPairStatusWorker(keyName: string, helpers: FormOnSubmitHelpers<any>) {
   let dialodId = DialogTypes.CreateOpenPGPKeyPair.id,
      actionType: ReturnType<typeof asyncType> = types.CREATE_OPEN_PGP_KEY_PAIR;

   try {
      let pollCount = 0;
      while (true) {
         pollCount++;
         const isActive = (yield call(isDialogActive, dialodId)) as SagaReturnType<
            typeof isDialogActive
         >;
         if (isActive) {
            helpers.setStatus({pollCount});
         }
         const response = (yield call(
            ApiService.getSecureJson,
            api.pgpKeyPair + `?keyName=${keyName}`
         )) as Response;
         if (response.status === 201) {
            const values = (yield response.json()) as IDiplomatObject;
            yield put(
               pushSuccessToast(
                  i18n.t('OpenPGP Keypair {{keyName}} successfully created.', {keyName})
               )
            );
            yield call(closeDialog, dialodId);
            yield put(action(actionType.SUCCESS));
            pollCount = 0;
            yield call(history.push, `/${packId(values.objectType, values.id)}`);
            break;
         }
         yield delay(1000);
      }
   } catch (e) {
      try {
         const {errors = []} = yield (e as Response).json();
         yield put(action(actionType.ERROR, errors));
         yield put(pushErrorToast(errors.map((e: any) => e.message)));
         const isActive = (yield call(isDialogActive, dialodId)) as SagaReturnType<
            typeof isDialogActive
         >;
         if (isActive) {
            yield helpers.setSubmitting(false);
         }
      } catch (e) {
         yield call(unhandedSagaError, e as Error);
      }
   }
}

export function* createOpenPGPKeyPairSaga({
   payload,
   callbacks,
}: Action<ICreateOpenPGPKeyPair, FormOnSubmitHelpers<any>>) {
   try {
      // yield call(ApiService.postSecureJson, api.pgpKeyPair, JSON.stringify(payload));
      // yield put(actions.startPollKeyPairStatus(payload!.keyName, callbacks!));

      const result: ICloseSubDialogAction<boolean> = yield call(openDialogWithResult, {
         id: DialogTypes.OpenPGPSubKeyCreation.id,
         data: {keyType: payload?.keyType},
      });
      console.debug('pgp result', result);
      if (result.payload?.data) {
         const result: ICloseSubDialogAction<IAddOpenPGPSubKey> = yield call(openDialogWithResult, {
            id: DialogTypes.AddOpenPGPSubkeys.id,
            data: payload,
         });

         const subkey = result.payload?.data;

         if (subkey) {
            const {keysDuration, createTime, keysCount} = subkey;
            let startTime = dayjs(createTime);

            const isYear = keysDuration % 365 === 0;

            for (let i = 0; i < keysCount; i++) {
               let lifetime = 0;
               let endDate;

               if (isYear) {
                  const yearsCount = keysDuration / 365;
                  endDate = startTime.add(yearsCount, 'year');
                  lifetime = endDate.diff(startTime, 'seconds');
               } else {
                  lifetime = convertDaysToSeconds(keysDuration);
                  endDate = dayjs(startTime).add(keysDuration, 'day');
               }

               const newSubkey = {
                  ...subkey,
                  createTime: startTime.toDate(),
                  lifetime,
               };
               payload?.subkeys.push(newSubkey);
               startTime = endDate;
            }
         }
      }

      yield call(ApiService.postSecureJson, api.pgpKeyPair, JSON.stringify(payload));

      //@ts-ignore
      yield call(pollKeyPairStatusWorker, payload!.keyName, callbacks);
      //   layoutActions.openSubDialog({
      //     id: DialogTypes.AddOpenPGPSubkey.id,
      //     data,
      //  })
   } catch (e) {
      console.error(e);
      const {errors = []} = yield (e as Response).json();
      yield put(pushErrorToast(errors.map((e: any) => e.message)));
      yield put(action(types.CREATE_OPEN_PGP_KEY_PAIR.ERROR, errors));
      const isActive = (yield select(
         selectors.isDialogActive,
         DialogTypes.CreateOpenPGPKeyPair.id
      )) as ReturnType<typeof selectors.isDialogActive>;
      if (isActive && callbacks) {
         yield callbacks.setSubmitting(false);
      }
   }
}
